import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import Fuse from 'fuse.js';
import { CardOneComponentConfig } from '../../utils/card-one';
import { ItemPerRow, CardOneListCardClickOutput } from '../../utils/card-one-list';
import { IconType } from '../../utils/icon';
import { InputGroupComponentConfig } from '../../utils/input-group';
import { SelectMode, SelectComponentConfig } from '../../utils/select';
import { TitleToolbarComponentConfig } from '../../utils/title-toolbar';
import { ButtonComponentConfig, ImageComponentConfig, MenuConfig, PaginationComponentConfig, SelectableOption, TextComponentConfig, IconComponentConfig } from '../../utils';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'rappider-cards',
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => RappiderCardsComponent),
      multi: true
    }
  ]
})
export class RappiderCardsComponent implements ControlValueAccessor, OnChanges, OnDestroy {

  @Input() title: TitleToolbarComponentConfig;
  @Input() items: CardOneComponentConfig[];
  @Input() itemCountPerRow = ItemPerRow.Three;
  @Input() isLoading = false;
  @Input() isLoadingCards = false;
  @Input() showTitleOnImage: boolean;
  @Input() showDescriptionOnImage: boolean;
  @Input() showSearchSection = true;
  @Input() showSearchFilterLabel = true;
  @Input() showCheckMarkOnCard: boolean;
  @Input() selectConfig: any;
  @Input() paginationConfig: PaginationComponentConfig;
  @Input() showPopover: boolean;
  @Input() showPopoverImage: ImageComponentConfig;
  @Input() isDynamicPagination = false;
  @Input() loadingMessage = 'Fetching data, please wait....';
  @Input() topSectionCards: CardOneComponentConfig[];
  @Input() topSectionHeight: string;
  @Input() bottomSectionHeight: string;
  @Input() menuConfig: any;
  @Input() menuConfigVisible: boolean;
  @Input() buttons: ButtonComponentConfig[];
  @Input() grouppedCards: SelectableOption[];
  @Input() clickedCardId: string;
  @Input() inputGroupConfig: InputGroupComponentConfig = {
    textbox: {
      placeholder: 'Search',
    },
    suffixIcon: {
      name: 'fas fa-xmark',
      type: IconType.FontAwesome,
      isClickable: true
    }
  };
  @Input() tagInputGroupConfig: InputGroupComponentConfig = {
    textbox: {
      placeholder: 'Search',
    },
    suffixIcon: {
      name: 'fas fa-xmark',
      type: IconType.FontAwesome,
      isClickable: true
    }
  };
  @Input() topCards: CardOneComponentConfig[];
  @Input() topCardsItemCountPerRow: ItemPerRow;
  @Input() bottomCards: CardOneComponentConfig[];
  @Input() bottomCardsItemCountPerRow: ItemPerRow;
  @Input() titleText: TextComponentConfig;
  @Input() detailedSearchActive: boolean;
  @Input() previewConfig = {
    modalVisibility: false,
    modalTitle: 'Preview',
    modalOkText: 'Add to Page',
    modalCancelText: 'Cancel',
    selectedTemplatePreview: null,
    width: '60%'
  };
  @Input() selfPreviewMode: boolean;
  @Input() categoryFilterCollapseVisible: boolean;
  @Input() tagFilterCollapseVisible: boolean;
  @Input() customIcon: IconComponentConfig;

  @Output() cardClick = new EventEmitter<any>();
  @Output() pageIndexChange = new EventEmitter<number>();
  @Output() searchTextChange = new EventEmitter<string>();
  @Output() tagFilterChange = new EventEmitter<string[]>();
  @Output() imageButtonClick = new EventEmitter<any>();
  @Output() avatarButtonClick = new EventEmitter<any>();
  @Output() menuItemClick = new EventEmitter<any>();
  @Output() topButtonsClick = new EventEmitter<any>();
  @Output() additionalButtonClick = new EventEmitter();
  @Output() cardDetail = new EventEmitter();
  @Output() topCardClick = new EventEmitter<any>();
  @Output() bottomCardClick = new EventEmitter<any>();
  @Output() searchBarIconClick = new EventEmitter<any>();
  @Output() modalConfirm = new EventEmitter<any>();
  @Output() selectedCategoriesEmit = new EventEmitter<any>();
  @Output() selectedTagsEmit = new EventEmitter<any>();

  _value: string;
  displayedData: any;
  selectedId: string;
  searchText: string;
  searchTexts: string[] = [];
  searchTextWithSearchButton: string;
  previousSearchText: string;
  selectedTags;
  selectedCategories;
  categoryFilterText: string;
  tagFilterText: string;
  displayedMenuConfig: any;
  tagDisplayedMenuConfig: any;
  searchedCategoriesFilter: any;
  searchedCategoriesTagFilter: any;
  emptyAnimationConfig = {
    path: 'assets/animations/no-search-result.json',
    loop: true
  };
  /* searchOptions for fuse */
  searchOptions = {
    threshold: 0.2,
    keys: [
      'titles.content',
      'descriptions.content',
      'descriptions.text',
      'additionalTags.text.text'
    ]
  };

  searchOptionsCategoryFilter = {
    threshold: 0.2,
    keys: [
      'key.text',
      'value',
    ]
  };

  get value() {
    return this._value;
  }

  set value(value: string) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.clickedCardId || changes.items) {
      this.getDisplayedData();
      this.markCardAsCheck(this.clickedCardId);
    }
    if (changes.menuConfig) {
      if (this.menuConfig?.items) {
        this.menuConfig.items = [...this.menuConfig?.items].sort((a, b) => {
          const firstObject = (a.key?.text ?? '').replace(/\s+/g, '');
          const secondObject = (b.key?.text ?? '').replace(/\s+/g, '');
          return firstObject.localeCompare(secondObject);
        });
        this.displayedMenuConfig = [...this.menuConfig?.items].sort((a, b) => {
          const firstObject = (a.key?.text ?? '').replace(/\s+/g, '');
          const secondObject = (b.key?.text ?? '').replace(/\s+/g, '');
          return firstObject.localeCompare(secondObject);
        });
      }
    }
    if (changes.selectConfig) {
      if (this.selectConfig?.options) {
        this.selectConfig.options = [...this.selectConfig?.options].sort((a, b) => {
          const firstObject = (a.key?.text ?? '').replace(/\s+/g, '');
          const secondObject = (b.key?.text ?? '').replace(/\s+/g, '');
          return firstObject.localeCompare(secondObject);
        });
        this.tagDisplayedMenuConfig = [...this.selectConfig?.options].sort((a, b) => {
          const firstObject = (a.key?.text ?? '').replace(/\s+/g, '');
          const secondObject = (b.key?.text ?? '').replace(/\s+/g, '');
          return firstObject.localeCompare(secondObject);
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.items?.forEach(conf => conf.isSelected = false);
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: string): void {
    this._value = value;
    this.selectedId = this._value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onTopSectionCardClick(data: any, item: CardOneComponentConfig) {
    this.onCardClick({ data, item });
  }

  onCardClick(output: CardOneListCardClickOutput) {
    if (this.selfPreviewMode) {
      this.previewConfig.modalVisibility = true;
      this.previewConfig.selectedTemplatePreview = output?.item;
    } else {
      this.cardDetail.emit(output);
      if (this.showCheckMarkOnCard) {
        this.items?.forEach(conf => conf.isSelected = false);

        if (this.selectedId === output.item.data.id) {
          this.selectedId = null;
        } else {
          this.selectedId = output?.item.data?.id;
          this.handleConfigWithSelectedId();
        }
      }
      this.value = this.selectedId;
      this.cardClick.emit(this.value);
    }

  }

  handleConfigWithSelectedId() {
    const selectedTemplateConfig = this.items?.find(conf => conf?.data?.id === this.selectedId);
    if (selectedTemplateConfig && this.showCheckMarkOnCard) {
      // selectedTemplateConfig.isSelected = true;
    }
  }

  /* Displaying data according to tag and search filtering */
  getDisplayedData() {
    let dataToDisplay = this.items || [];

    if (!this.selectedTags) {
      this.selectedTags = [];
    }
    if (!this.selectedCategories) {
      this.selectedCategories = [];
    }
    if (!this.menuConfig?.items) {
      this.menuConfig = { items: [] };
    }
    if (!this.selectConfig?.options) {
      this.selectConfig = { options: [] };
    }

    if (this.selectedCategories && this.selectedCategories.length > 0 && !this.isDynamicPagination) {
      dataToDisplay = dataToDisplay.filter(item =>
        (item as any).category && this.selectedCategories.includes((item as any).category)
      );
      this.searchedCategoriesFilter = this.menuConfig.items.filter(item =>
        this.selectedCategories.includes(item.value)
      );
    } else {
      this.searchedCategoriesFilter = [];
    }

    if (this.selectedCategories && this.selectedCategories.length > 0 && this.isDynamicPagination) {
      this.searchedCategoriesFilter = this.menuConfig.items.filter(item =>
        this.selectedCategories.includes(item.value)
      );
    } else {
      this.searchedCategoriesFilter = [];
    }

    if (this.selectedTags && this.selectedTags.length > 0 && this.isDynamicPagination) {
      this.searchedCategoriesTagFilter = this.selectConfig.options.filter(item =>
        this.selectedTags.includes(item.value)
      );
    } else if (this.isDynamicPagination) {
      this.searchedCategoriesTagFilter = [];
    }

    if (this.selectedTags && this.selectedTags.length > 0 && !this.isDynamicPagination) {
      if (this.selectConfig.settings?.mode &&
        [SelectMode.Multiple, SelectMode.Tags, SelectMode.OptionSelect].includes(this.selectConfig.settings.mode)) {
        dataToDisplay = dataToDisplay.filter(item =>
          item.additionalTags && item.additionalTags.some(tag =>
            tag.text && tag.text.text && this.selectedTags.includes(tag.text.text)
          )
        );
      } else {
        const selectedTag = Array.isArray(this.selectedTags) ? this.selectedTags[0] : this.selectedTags;
        dataToDisplay = dataToDisplay.filter(item =>
          item.additionalTags && item.additionalTags.some(tag =>
            tag.text && tag.text.text === selectedTag
          )
        );
      }
      this.searchedCategoriesTagFilter = this.selectConfig.options.filter(item =>
        this.selectedTags.includes(item.value)
      );
    } else if (!this.isDynamicPagination) {
      this.searchedCategoriesTagFilter = [];
    }

    if (this.searchText && this.searchText !== this.previousSearchText) {
      this.previousSearchText = this.searchText;
      this.searchTextChange.emit(this.searchText);
    }

    if (this.searchText && this.searchText.trim() && !this.isDynamicPagination) {
      this.displayedData = this.searchByOptions(dataToDisplay, this.searchOptions, this.searchText);
    } else {
      this.displayedData = dataToDisplay;
    }
  }

  /* Paginates the displayed data */
  getDataWithPagination(grouppedCardId?) {
    if (!this.grouppedCards?.length) {
      return this.applyPagination(this.displayedData);
    }

    if (!grouppedCardId) {
      return this.displayedData;
    }

    // Modüle göre kartları filtrele
    const filteredData = this.displayedData?.filter(card => card?.data?.moduleId === grouppedCardId);

    // Eğer bu modülde hiç kart bulunamazsa, boş array döndür
    // Bu sayede modül başlığı görünür ama içi boş olur
    return filteredData || [];
  }

  private applyPagination(data: any[]) {
    if (!this.paginationConfig?.showPagination) {
      return data;
    }

    if (this.isDynamicPagination) {
      return data;
    }

    const { pageIndex, pageSize } = this.paginationConfig;
    const startIndex = (pageIndex - 1) * pageSize;
    const endIndex = pageIndex * pageSize;

    return data?.slice(startIndex, endIndex);
  }

  searchByOptions(list: any, options: Fuse.IFuseOptions<unknown>, pattern: string): any {
    if (!pattern) {
      return list.filter((item, index, originalArray) =>
        index === originalArray.findIndex(t => t.data?.id === item.data?.id)
      );
    } else {
      const fuse = new Fuse(list, options);

      const removeDuplicates = (results: any[]): any[] => {
        const seen = new Set();
        return results.filter(result => {
          let identifier;
          if (result.item.data?.id) {
            identifier = result.item.data.id;
            if (seen.has(identifier)) {
              return false;
            } else {
              seen.add(identifier);
              return true;
            }
          } else if (result.item.value) {
            identifier = result.item.value;
            if (seen.has(identifier)) {
              return false;
            } else {
              seen.add(identifier);
              return true;
            }
          }
        });
      };

      const searchWithMultipleWords = (searchQuery: string) => {
        const queryWords = searchQuery.trim().split(/\s+/);
        let filteredResults = [];

        queryWords.forEach(word => {
          const results = fuse.search(word);
          filteredResults = [...filteredResults, ...results];
        });

        return removeDuplicates(filteredResults);
      };

      return searchWithMultipleWords(pattern)?.map(item => item.item);
    }
  }

  /* Selected tags come as parameters and are defined to be filtered. */
  onSelectedTagsChange(selectedTags) {
    this.selectedTags = selectedTags;
    this.selectConfig.options = this.selectConfig?.options.map(m => (selectedTags.includes(m.value) && !m.isSelected) ? { ...m, isSelected: true } : m);
    this.tagDisplayedMenuConfig = this.tagDisplayedMenuConfig.map(m => (selectedTags.includes(m.value) && !m.isSelected) ? { ...m, isSelected: true } : m);
    if (!this.isDynamicPagination) {
      this.getDisplayedData();
    }
    this.selectedTagsEmit.emit(this.selectedTags);
  }

  onSelectedCategoryChange(selectedCategories) {
    this.selectedCategories = selectedCategories;
    this.menuConfig.items = this.menuConfig?.items.map(m => (selectedCategories.includes(m.value) && !m.isSelected) ? { ...m, isSelected: true } : m);
    this.displayedMenuConfig = this.displayedMenuConfig.map(m => (selectedCategories.includes(m.value) && !m.isSelected) ? { ...m, isSelected: true } : m);
    if (!this.isDynamicPagination) {
      this.getDisplayedData();
    }
    this.selectedCategoriesEmit.emit(this.selectedCategories);
  }

  /* Page index comes as a parameter and is defined to be paginated */
  onPageIndexChange(pageIndex: number) {
    this.paginationConfig.pageIndex = pageIndex;
    this.pageIndexChange.emit(pageIndex);
  }

  onImageButtonClick(imageButtonData) {
    if (this.selfPreviewMode) {
      this.previewConfig.modalVisibility = true;
      this.previewConfig.selectedTemplatePreview = imageButtonData?.item;
    }
    this.imageButtonClick.emit({ imageButtonData });
  }

  onAvatarButtonClick(avatarButtonData) {
    this.avatarButtonClick.emit({ avatarButtonData });
  }
  onMenuItemClick(menuItem) {
    this.menuItemClick.emit(menuItem);
  }

  onTopButtonsClick(buttons) {
    this.topButtonsClick.emit(buttons);
  }

  onPreviewAdditionalButtonClick(previewAdditionalButton) {
    if (this.selfPreviewMode) {
      this.modalConfirm.emit(this.previewConfig.selectedTemplatePreview);
    }
  }

  onAdditionalButtonClick(additionalButton) {
    this.additionalButtonClick.emit(additionalButton);
  }

  markCardAsCheck(selectedId) {
    if (!this.showCheckMarkOnCard) {
      return;
    }
    this.items?.forEach(conf => conf.isSelected = false);
    const selectedTemplateConfig = this.items?.find(conf => conf?.data?.id === selectedId);
    if (selectedTemplateConfig) {
      selectedTemplateConfig.isSelected = true;
    }
  }

  onTopCardClick(output: CardOneListCardClickOutput) {
    this.topCardClick.emit(output);
  }

  onBottomCardClick(output: CardOneListCardClickOutput) {
    this.bottomCardClick.emit(output);
  }

  onSearchBarIconClick() {
    this.searchTextWithSearchButton = '';
    this.searchText = '';
    this.previousSearchText = this.searchText;
    this.searchTextChange.emit(this.searchText);
    this.getDisplayedData();
  }

  onItemSelected(output) {
    this.menuConfig.items = this.menuConfig.items.map(item => ({
      ...item,
      isSelected: output.includes(item.value)
    }));
  }

  onCategoryFilterTextIconClick() {
    this.categoryFilterText = '';
    this.displayedMenuConfig = this.menuConfig.items;
    this.getDisplayedData();
  }

  onTagFilterTextIconClick() {
    this.tagFilterText = '';
    this.tagDisplayedMenuConfig = this.selectConfig.options;
    this.getDisplayedData();
  }

  onChangeCategoryFilterText(categoryFilterText) {
    this.categoryFilterText = categoryFilterText;
    if (this.categoryFilterText === '') {
      this.displayedMenuConfig = this.menuConfig.items;
    } else {
      this.displayedMenuConfig = this.searchByOptions(this.menuConfig.items, this.searchOptionsCategoryFilter, this.categoryFilterText);
    }
    this.getDisplayedData();
  }

  onChangeTagFilterText(tagFilterText) {
    this.tagFilterText = tagFilterText;
    if (this.tagFilterText === '') {
      this.tagDisplayedMenuConfig = this.selectConfig.options;
    } else {
      this.tagDisplayedMenuConfig = this.searchByOptions(this.selectConfig.options, this.searchOptionsCategoryFilter, this.tagFilterText);
    }
    this.getDisplayedData();
  }

  onCategoryClick(category) {
    this.selectedCategories = this.selectedCategories.filter(c => c !== category);
    this.menuConfig.items = this.menuConfig?.items.map(m => m.value === category ? { ...m, isSelected: false } : m);
    this.displayedMenuConfig = this.displayedMenuConfig.map(m => m.value === category ? { ...m, isSelected: false } : m);
    if (!this.isDynamicPagination) {
      this.getDisplayedData();
    }
    if (this.isDynamicPagination) {
      this.selectedCategoriesEmit.emit(this.selectedCategories);
    }
  }

  onTagClick(tag) {
    this.selectedTags = this.selectedTags.filter(t => t !== tag);
    this.selectConfig.options = this.selectConfig?.options.map(t => t.value === tag ? { ...t, isSelected: false } : t);
    this.tagDisplayedMenuConfig = this.tagDisplayedMenuConfig.map(t => t.value === tag ? { ...t, isSelected: false } : t);
    if (!this.isDynamicPagination) {
      this.getDisplayedData();
    }
    if (this.isDynamicPagination) {
      this.selectedTagsEmit.emit(this.selectedTags);
    }
  }

  onSearchButtonClick(searchText) {
    this.searchText = searchText;
    if (!this.isDynamicPagination) {
      this.splitStringToArray();
      this.getDisplayedData();
    }
    if (this.isDynamicPagination) {
      this.splitStringToArray();
      this.searchTextChange.emit(this.searchText);
    }
  }

  previewModalCancelClick() {
    this.previewConfig.modalVisibility = false;
  }

  previewModalOkClick() {
    this.modalConfirm.emit(this.previewConfig.selectedTemplatePreview);
    this.previewConfig.modalVisibility = false;
  }

  clearAll() {
    this.selectedCategories = [];
    this.menuConfig.items = this.menuConfig?.items.map(m => ({ ...m, isSelected: false }));
    this.displayedMenuConfig = this.displayedMenuConfig.map(m => ({ ...m, isSelected: false }));
    this.selectedTags = [];
    this.selectConfig.options = this.selectConfig?.options.map(t => ({ ...t, isSelected: false }));
    this.tagDisplayedMenuConfig = this.tagDisplayedMenuConfig.map(t => ({ ...t, isSelected: false }));
    this.searchTextWithSearchButton = '';
    this.searchText = '';
    this.previousSearchText = this.searchText;
    this.searchTextChange.emit(this.searchText);
    if (!this.isDynamicPagination) {
      this.getDisplayedData();
    }
    if (this.isDynamicPagination) {
      this.selectedCategoriesEmit.emit(this.selectedCategories);
      this.selectedTagsEmit.emit(this.selectedTags);
      this.searchTextChange.emit(this.searchText);
    }
  }

  splitStringToArray() {
    this.searchTexts = [...new Set([this.searchText, ...this.searchText.split(' ')])];
    this.getDisplayedData();
  }

  onRemoveSearchClick(searchText) {
    this.searchTexts = this.searchText.split(' ');
    const textToRemove = searchText;
    this.searchText = this.searchTexts.filter(text => text !== textToRemove).join(' ').trim();
    this.searchTextWithSearchButton = this.searchText;
    this.splitStringToArray();
    if (!this.isDynamicPagination) {
      this.getDisplayedData();
    }
    if (this.isDynamicPagination) {
      this.searchTextChange.emit(this.searchText);
    }
  }
}
